import { useEffect, useMemo } from 'react';
import { useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import type { Wallet } from '@asia/shared';
import { useSession } from './useSession';
import { fetchBalances, fetchTxs, watchAddresses, unwatchAddresses, type Balances } from '../lib/tron';
import { readBalanceCache, writeBalanceCache } from '../lib/balance-cache';

/** Subscribe to server-pushed balance/tx updates for an address while mounted.
 *  History is not polled here — chain-watch already hits TronGrid and pushes
 *  `txs` / `balances` over the WebSocket. We only refetch on Telegram resume /
 *  tab focus so a missed push while backgrounded still catches up. */
function useWatch(address: string | undefined) {
  const { token } = useSession();
  const qc = useQueryClient();
  useEffect(() => {
    if (!token || !address) return;
    void watchAddresses([address]).catch(() => {});
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        void qc.invalidateQueries({ queryKey: ['txs', address] });
        void qc.invalidateQueries({ queryKey: ['balances', address] });
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      void unwatchAddresses([address]).catch(() => {});
    };
  }, [token, address, qc]);
}

/** Live on-chain USDT + TRX balances for an address (initial fetch over RPC, then
 *  realtime via push). Disabled in dev/browser (no session) — mock baseline used.
 *  Last known amounts are shown immediately from localStorage (SWR). */
export function useBalances(address: string | undefined) {
  const { token } = useSession();
  useWatch(address);
  return useQuery({
    queryKey: ['balances', address],
    enabled: !!token && !!address,
    queryFn: async () => {
      const bal = await fetchBalances(token!, address!);
      writeBalanceCache(address!, bal);
      return bal;
    },
    placeholderData: () => (address ? readBalanceCache(address) : undefined),
    staleTime: 20_000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Spendable USDT/TRX for a wallet. Real sessions always read the chain —
 * `wallet.coins[].balance` is zeroed for server wallets and must not be used
 * for Max / insufficient checks (see `serverToApp` in the wallets store).
 * Dev/browser (no token) falls back to mock coin balances.
 */
export function useWalletBalances(wallet: Wallet | undefined): Balances {
  const { token } = useSession();
  const chain = useBalances(wallet?.address).data;
  return useMemo(() => {
    if (token) return { usdt: chain?.usdt ?? 0, trx: chain?.trx ?? 0 };
    const coin = (sym: string) =>
      wallet?.coins.find((c) => c.symbol === sym)?.balance ?? 0;
    return { usdt: coin('USDT'), trx: coin('TRX') };
  }, [token, chain?.usdt, chain?.trx, wallet]);
}

/** On-chain transfers (USDT + TRX) for an address, newest first, paginated by a
 *  timestamp cursor for infinite scroll. Exposes a flattened, de-duped `data`
 *  array plus the load-more controls. */
export function useTxs(address: string | undefined) {
  const { token } = useSession();
  const q = useInfiniteQuery({
    queryKey: ['txs', address],
    enabled: !!token && !!address,
    queryFn: ({ pageParam }) => fetchTxs(token!, address!, pageParam),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => last.nextBefore ?? undefined,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  // Flatten pages and drop any duplicate ids that can appear at page boundaries.
  const data = useMemo(() => {
    if (!q.data) return undefined;
    const seen = new Set<string>();
    const out = [];
    for (const page of q.data.pages) {
      for (const t of page.txs) {
        if (seen.has(t.id)) continue;
        seen.add(t.id);
        out.push(t);
      }
    }
    return out;
  }, [q.data]);

  return {
    data,
    isLoading: q.isLoading,
    hasNextPage: q.hasNextPage,
    isFetchingNextPage: q.isFetchingNextPage,
    fetchNextPage: q.fetchNextPage,
  };
}

export type { Balances };
